// Terms of service + privacy policy, rendered by buildLegal and
// reached from the fixed bottom-left links. Written to reflect what
// the code ACTUALLY does — when data practices change (new tables,
// new third parties, contact policies), update the relevant section
// AND the lastUpdated date in the same commit.

export const LEGAL_UPDATED = '2026-06-11';
export const LEGAL_CONTACT = 'admin@angryjuniper.cloud';

export interface LegalSection { title: string; body: string[] }

export const TERMS: LegalSection[] = [
  {
    title: 'acceptance of terms',
    body: [
      'settle is a free browser game provided by its maintainer ("we", "us"). by loading or playing settle, creating an account, submitting scores, chatting, or sending feedback, you agree to these terms. if you do not agree, do not use the software.',
    ],
  },
  {
    title: 'alpha software',
    body: [
      'settle is pre-release software under active development. features may change, break, or disappear without notice. saved data — including local profiles, settings, statistics, accounts, and leaderboard entries — may be reset or lost at any time. we make no commitment to data durability during the alpha period.',
    ],
  },
  {
    title: 'accounts',
    body: [
      'accounts are optional and exist to reserve a leaderboard name and sync your save across devices. you are responsible for keeping your password secret and for activity that happens under your account. usernames that impersonate others or that are abusive, hateful, or obscene may be renamed or removed without notice.',
      'an email address is optional and is used solely for password recovery. accounts without an email cannot self-recover a lost password.',
      'registration asks whether you are 13 years of age or older, and you agree to answer truthfully. accounts registered as under 13 can play and compete normally, but chat is disabled for them (see the privacy policy). misrepresenting your age to access chat is a violation of these terms.',
    ],
  },
  {
    title: 'fair play + conduct',
    body: [
      'leaderboard submissions must come from genuine play. automating gameplay, tampering with score submissions, exploiting bugs for ranking, or interfering with the service (including its chat) may result in entries, messages, or accounts being removed and access being blocked.',
      'chat is shared space: no harassment, hate speech, spam, illegal content, or personal information about other people. messages may be removed and chat access revoked at our discretion.',
    ],
  },
  {
    title: 'no warranty',
    body: [
      'settle is provided "as is" and "as available", without warranty of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. we do not warrant that the game will be uninterrupted, error-free, or secure.',
    ],
  },
  {
    title: 'limitation of liability',
    body: [
      'to the maximum extent permitted by law, we will not be liable for any indirect, incidental, special, consequential, or exemplary damages, or for any loss of data, arising from or related to your use of settle. your sole remedy for dissatisfaction with the game is to stop using it.',
    ],
  },
  {
    title: 'changes',
    body: [
      'we may update these terms as the game evolves. material changes will be reflected in the "last updated" date below and noted in the changelog. continued use after a change constitutes acceptance.',
    ],
  },
  {
    title: 'contact',
    body: [
      `questions about these terms: ${LEGAL_CONTACT}.`,
    ],
  },
];

export const PRIVACY: LegalSection[] = [
  {
    title: 'the short version',
    body: [
      'settle collects the minimum needed to run a leaderboard, accounts, chat, and a feedback inbox. we will not contact you unless you contact us first. we do not sell, share, or disclose any data in our possession to anyone, and we never will without telling you in this policy first.',
    ],
  },
  {
    title: 'data stored only on your device',
    body: [
      'local profiles, game settings, statistics, personal bests, and your anonymous presence identifier live in your browser\'s local storage. they never leave your device unless a specific feature below sends a specific piece of them.',
    ],
  },
  {
    title: 'accounts',
    body: [
      'if you create an account we store: your username, a salted cryptographic hash of your password (never the password itself), an optional email address, and timestamps. the email is used exclusively to send password-reset codes you request — it is never used for outreach, newsletters, or anything else, and is never shared.',
    ],
  },
  {
    title: 'leaderboard + presence + chat',
    body: [
      'score submissions store your account username, the mode, the value, and a timestamp, displayed publicly on the leaderboard. presence heartbeats store a transient identifier and a timestamp to compute the "settlers online" count; stale entries are deleted within minutes. chat messages store your username, the message text, and a timestamp, and are visible to other players; old messages are periodically deleted.',
    ],
  },
  {
    title: 'cloud saves',
    body: [
      'if you sign in, your game save — settings, statistics, and personal bests — is stored in our database (cloudflare d1), keyed to your account, for the sole purpose of letting you continue playing from any device. this save data is anonymized gameplay information: it contains no contact details, no location, and no device identifiers, and it is never used for anything except restoring your game. it is deleted with your account on request.',
    ],
  },
  {
    title: "children's privacy + chat",
    body: [
      'settle is a general-audience game. registration asks whether you are 13 years of age or older. consistent with child-safety laws such as the u.s. children\'s online privacy protection act (coppa), accounts registered as under 13 cannot see or use chat — preventing the collection or public display of messages from children — and we do not knowingly collect personal information from children under 13. the email field is optional for everyone and is used only for password recovery; we encourage younger players to register without one.',
      'an optional profanity filter (on by default) masks strong language in chat for all players. if you believe a child has provided personal information through the game, contact us and we will delete it promptly.',
    ],
  },
  {
    title: 'feedback reports (the only place we log system data)',
    body: [
      'when — and only when — you submit feedback, we receive and store: your message, an optional screenshot you attach, a system snapshot shown to you before sending (game version, browser, operating system, display, gpu, locale), and connection details added at our edge: your ip address, approximate location (city / country), network provider, and browser user-agent string. ip address and approximate location are personally identifiable information; they are collected for the express purpose of debugging the issue you reported, are never used to contact or profile you, and are never shared with anyone.',
    ],
  },
  {
    title: 'infrastructure providers',
    body: [
      'settle is hosted on cloudflare (pages, workers, d1); cloudflare processes requests — including ip addresses — as our infrastructure provider and applies its own bot-protection at the network edge. password-reset codes and feedback emails are delivered through resend, which processes the email address and message contents in transit. both act as processors for the purposes above; we do not send them data beyond what those features require.',
    ],
  },
  {
    title: 'cookies + tracking',
    body: [
      'settle sets no advertising or analytics cookies and embeds no trackers. there is no analytics suite, no fingerprinting, and no third-party advertising of any kind.',
    ],
  },
  {
    title: 'retention + removal',
    body: [
      `feedback logs are kept only as long as useful for debugging. to have your account, leaderboard entries, chat messages, or feedback reports corrected or deleted, email ${LEGAL_CONTACT} from the address on your account (or include your username) and we will handle it manually.`,
    ],
  },
  {
    title: 'changes',
    body: [
      'if data practices change, this policy and its "last updated" date change with them, and the changelog will say so.',
    ],
  },
  {
    title: 'asset credits',
    body: [
      "settle uses third-party art and audio under license. attributions:",
      "• animated cat sprite: aichan's pixel cat pack (https://aichan.itch.io). used under the asset license; the original file ships unmodified and is recolored at runtime only.",
    ],
  },
  {
    title: 'contact',
    body: [
      `privacy questions or removal requests: ${LEGAL_CONTACT}.`,
    ],
  },
];
